import configs from '@/config'
// Sentry
import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'
import { Replay } from '@sentry/replay'
import { logger, getDeviceId } from '@ahaslides-product/frontend-toolkit'

const ignoreErrors = [
  'zaloJSV2 is not defined',
  'Can\'t find variable: zaloJSV2',
  'ResizeObserver loop limit exceeded',
  'Cannot read property \'getReadModeConfig\' of undefined',
  'Cannot read property \'getReadModeRender\' of undefined',
  'Cannot read property \'getReadModeExtract\' of undefined',
  'UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:2',
]

// AHA-40930: browser messages for a fetch()/XHR that could not complete the
// network round-trip — offline, dropped connection, request aborted by
// navigation, or blocked by an extension. They are transport-level failures
// (never an HTTP error status, which resolves with res.ok === false) and across
// the audience app are caught and recovered from gracefully at ~30 call sites.
// Reporting these handled, environmental failures is pure noise (a single
// stackless Sentry group of ~20k events from many sources). staging already
// drops the Safari `Load failed` variant in shouldDropEvent; this covers the
// Chromium/Firefox equivalents, gated on the error being handled.
const NETWORK_ERROR_MESSAGES = [
  'Failed to fetch', // Chromium / Samsung Internet / WebView
  'NetworkError when attempting to fetch resource', // Firefox
  'Load failed', // Safari / iOS WebKit (fetch)
  'Network error', // XHR fallback (utils/language)
  'Loading chunk', // webpack / rspack lazy chunk that never arrived
]

// Drop an event only when it is BOTH a network-transport failure AND already
// handled (caught + captured). A genuinely uncaught network error still
// surfaces, so we don't mask a real bug (e.g. a missing await). A call site that
// already retried and gave up tags the capture `retriesExhausted` — that is a
// sustained outage, not a transient blip, so it is always kept.
export const isHandledNetworkError = (error, event) => {
  if (event?.tags?.retriesExhausted) return false

  const message = (error && error.message) || (typeof error === 'string' ? error : '')
  const isNetworkError = NETWORK_ERROR_MESSAGES.some((m) => message.includes(m))
  if (!isNetworkError) return false

  const handled = event?.exception?.values?.[0]?.mechanism?.handled
  // captureException sets handled: true; treat a missing mechanism as handled.
  return handled !== false
}

const replay = new Replay({ maskAllText: false })

const isResponseShaped = (error) => (
  !!error
  && typeof error === 'object'
  && !(error instanceof Error)
  && Object.prototype.hasOwnProperty.call(error, 'status')
  && ('ok' in error || 'headers' in error)
)

const truncate = (text, max = 500) => (
  typeof text === 'string' && text.length > max ? `${text.slice(0, max)}...` : text
)

// Sentry can't serialise a raw Response-shaped object, so every catch block that
// captures one collapses into a single unactionable `<object>.value` cluster.
// Relabel it into a groupable HTTP-status signal instead of dropping it.
export const normaliseResponseException = (event, hint) => {
  try {
    const error = hint?.originalException

    if (!isResponseShaped(error)) return event

    const { status, ok, bodyText } = error
    const url = error.url ?? error.config?.url ?? error.request?.responseURL

    const message = `HTTP ${status}${url ? ` ${url}` : ''}`

    if (event.exception?.values?.[0]) {
      event.exception.values[0].type = 'HttpResponseError'
      event.exception.values[0].value = message
    } else {
      event.message = message
    }

    event.fingerprint = ['http-response', String(status)]
    event.tags = { ...event.tags, http_status: status }
    event.extra = {
      ...event.extra,
      status,
      ok,
      url,
      bodyText: truncate(bodyText),
    }

    return event
  } catch {
    return event
  }
}

const TRANSIENT_RESPONSE_STATUSES = new Set([401, 403, 404, 408, 409, 429])

const isVueResourceResponse = (error) => (
  error != null &&
  typeof error === 'object' &&
  !(error instanceof Error) &&
  typeof error.status === 'number' &&
  ('ok' in error || 'bodyText' in error || 'headers' in error || 'body' in error)
)

// For call sites that hide their vue-resource Response inside `extra` rather than
// passing it as the captured exception — beforeSend's classifyEvent never sees it
// there, so the drop never fires. Reuses the same expected-transient definition
// (network cut-off + TRANSIENT_RESPONSE_STATUSES) so a call site can skip the
// Sentry.capture* call entirely instead of duplicating the classification.
export const isExpectedTransientResponse = (error) => (
  error?.status === 0 || (isVueResourceResponse(error) && TRANSIENT_RESPONSE_STATUSES.has(error.status))
)

// vue-resource rejects its promise with the raw HTTP Response object (not an
// Error) on any non-2xx/failed request; the ~159 `catch { captureException }`
// sites then hand Sentry that object, which all collapses into one useless
// `<object>.value` issue. Classify at this single choke point: drop expected
// transients, relabel genuine faults into one readable, correctly-grouped issue.
export const classifyEvent = (event, hint) => {
  const error = hint?.originalException

  if (error instanceof Error) return event

  // network cut-off — Response carries status 0 (preserved from PR #594)
  if (error?.status === 0) return null

  if (isVueResourceResponse(error)) {
    const { status } = error
    if (TRANSIENT_RESPONSE_STATUSES.has(status)) return null

    const url = typeof error.url === 'string' ? error.url : undefined
    const endpoint = url ? url.split('?')[0] : undefined
    const bodyText = typeof error.bodyText === 'string' ? error.bodyText.slice(0, 500) : undefined

    event.exception = {
      ...event.exception,
      values: [{
        ...event.exception?.values?.[0],
        type: 'HttpResponseError',
        value: `HTTP ${status}${endpoint ? ` ${endpoint}` : ''}`,
      }],
    }
    event.fingerprint = ['http-response-error', String(status), endpoint || 'unknown']
    event.extra = {
      ...event.extra,
      httpResponse: { status, url: endpoint, bodyText },
    }
    return event
  }

  return event
}

// A SPA's live location drifts from where a frame's script was loaded (the
// audience navigates client-side without a reload), so comparing a frame's URL
// against window.location is unreliable — a single trace routinely carries
// several distinct page paths. Same-origin-but-not-a-.js-file is the stable
// signal instead: the injected script has no bundle asset of its own, while a
// real recursion in our code always carries at least one bundle chunk frame.
const isSameOriginNonJsFrame = (filename) => {
  if (typeof filename !== 'string' || typeof window === 'undefined' || !window.location) return false
  try {
    const url = new URL(filename, window.location.origin)
    return url.origin === window.location.origin && !url.pathname.toLowerCase().endsWith('.js')
  } catch {
    return false
  }
}

// Chrome-on-iOS's in-app-browser injects a user script (__gCrWeb) that can enter
// its own mutually-recursive loop; every frame reports a same-origin page path,
// never a bundle asset.
export const isInjectedScriptRecursion = (event, hint) => {
  try {
    const error = hint?.originalException
    const eventException = event?.exception?.values?.[0]
    const name = error?.name || eventException?.type
    const message = error?.message || eventException?.value || ''
    if (name !== 'RangeError' || !message.includes('Maximum call stack size exceeded')) return false

    const frames = eventException?.stacktrace?.frames
    if (!Array.isArray(frames) || frames.length === 0) return false

    return frames.every((frame) => {
      const filename = frame?.filename || frame?.abs_path
      return !filename || isSameOriginNonJsFrame(filename)
    })
  } catch {
    return false
  }
}

const ZOID_COMPONENT_CLOSED_MESSAGE = 'Component closed'

// Frames are minified bundle paths in the browser (source maps resolve on Sentry's side), so a zoid frame cannot be matched here.
export const isZoidInternalComponentClosedEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => (
    value?.type === 'Error' &&
    value.value === ZOID_COMPONENT_CLOSED_MESSAGE &&
    value.mechanism?.handled === false
  ))
}

// zoid's window `unload` listener destroys every mounted plugin with this error when
// the audience leaves the page. Frames are minified in the browser, so match the
// message alone: zoid is the only code that raises it (node_modules and src).
const ZOID_WINDOW_NAVIGATED_AWAY_MESSAGE = 'Window navigated away'

export const isZoidWindowNavigatedAwayEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => value?.type === 'Error' && value.value === ZOID_WINDOW_NAVIGATED_AWAY_MESSAGE)
}

const isBlobFrame = (frame) => (
  typeof frame?.filename === 'string' && frame.filename.startsWith('blob:')
)

// A third-party inline worker (Crazy Egg's CSP probe) runs importScripts() from a
// blob: URL with no error handler, so an offline device re-fires its NetworkError
// on window.onerror. Our own blob worker (mqtt worker-timers) never imports scripts.
export const isBlobWorkerScriptImportFailure = (event) => {
  const value = event?.exception?.values?.[0]
  if (typeof value?.value !== 'string' || !value.value.startsWith('NetworkError')) return false

  const frames = value.stacktrace?.frames
  return Array.isArray(frames) && frames.length > 0 && frames.every(isBlobFrame)
}

export const shouldDropEvent = (hint, event) => {
  const error = hint?.originalException
  const name = error?.name
  const message = error?.message || ''

  // iOS Safari can reject AudioContext.resume() with this exact type+message
  // when the audio hardware is busy/interrupted — an expected, non-actionable
  // condition. Matching on both fields keeps this from masking anything else.
  if (name === 'InvalidStateError' && message.includes('Failed to start the audio device')) return true

  // Safari's opaque network-layer failure for a fetch/resource load (our request
  // layer appends the host): a client connectivity or CDN drop, never an app
  // fault, flooding Sentry from mobile audiences. Anchored to the start so a
  // genuine app error that merely contains the phrase still reports.
  if (name === 'TypeError' && message.startsWith('Load failed')) return true

  // The Chromium sibling of the Safari case above: an unhandledrejection from
  // a failed fetch (CDN/network drop), raised via src/instrument/fetch.ts —
  // never an app fault. Same anchoring rationale.
  if (name === 'TypeError' && message.startsWith('Failed to fetch')) return true

  // Home.vue's validateAcessCode() throws this itself when the presentation
  // lookup resolves with an empty body — the audience typed a wrong/expired
  // access code, the single expected outcome of that form. Exact match on
  // both fields: a plain `Error` is otherwise a catch-all type, so anchoring
  // to this literal message keeps any other Error still reporting.
  if (name === 'Error' && message === 'No presentation found') return true

  // Browser autoplay policy rejects a slide's audio play() that runs before a
  // user gesture. AudioControl.vue already recovers (surfaces the unmute
  // control), so the capture is pure noise. Two message variants of the one
  // policy — Safari ("not allowed by the user agent") and Chromium ("play()
  // failed …") — anchored so any other NotAllowedError (clipboard, fullscreen)
  // still reports.
  if (name === 'NotAllowedError'
    && (message.startsWith('The request is not allowed by the user agent') || message.includes('play() failed'))) return true

  if (isInjectedScriptRecursion(event, hint)) return true

  if (isBlobWorkerScriptImportFailure(event)) return true

  if (isZoidWindowNavigatedAwayEvent(event)) return true

  return false
}

const init = () => {
  if (!configs.isSentryEnabled) return

  Sentry.init({
    release: configs.sentryRelease,
    dsn: configs.sentry,
    environment: configs.env,
    integrations: [
      browserTracingIntegration(),
      replay,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0,
    Vue,
    ignoreErrors,
    denyUrls: [
      /analytics\.tiktok\.com/i,
    ],
    tracingOptions: {
      trackComponents: true,
    },
    enableLogs: true,
    beforeSend(event, hint) {
      const { originalException: error } = hint

      if (configs.env !== 'production') {
        // log error to browser console
        // for better visibility into exceptions
        if (event?.extra?.error) {
          // eslint-disable-next-line no-console
          logger(event.extra.error)
        } else if (error) {
          // eslint-disable-next-line no-console
          logger(error)
        }
      }
      // ignore sentry error due to network cut-off
      if (error?.status === 0) return null
      // AHA-40930: drop handled, transient network-transport failures (noise)
      if (isHandledNetworkError(error, event)) return null
      if (isZoidInternalComponentClosedEvent(event)) return null
      if (shouldDropEvent(hint, event)) return null
      return classifyEvent(event, hint)
    },
  })
}

const captureException = (error, options) => {
  if (!configs.isSentryEnabled) return

  try {
    // Preserve a real Error's stack and hand a vue-resource Response over as is, so
    // beforeSend's classifyEvent can drop or relabel it; only synthesize one when handed a string/other.
    const exception = error instanceof Error || isVueResourceResponse(error) ? error : new Error(error)
    Sentry.captureException(exception, options)
  } catch {
    // do nothing
  }
}

const addBreadcrumb = (breadcrumb = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    const editedBreadcrumb = { ...breadcrumb }

    if (breadcrumb.level === 'info') {
      editedBreadcrumb.level = 'info'
    }

    Sentry.addBreadcrumb(editedBreadcrumb)
  } catch {
    // do nothing
  }
}

const startSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.flush()
  } catch (error) {
    captureException(error)
  }
}

const stopSessionReplay = async () => {
  if (!configs.isSentryEnabled) return

  try {
    await replay.stop()
  } catch (error) {
    captureException(error)
  }
}

const getTabId = () => {
  let tabId = sessionStorage.getItem('aha-tab-id')
  if (!tabId) {
    tabId = new Date().getTime()
    sessionStorage.setItem('aha-tab-id', tabId)
  }
  return tabId
}

const augmentData = (data) => {
  if (!data) return data
  return {
    ...data,
    deviceId: getDeviceId(),
    tabId: getTabId(),
  }
}

const setTags = (attributes = {}) => {
  if (!configs.isSentryEnabled) return

  try {
    // Set as tags for filtering and searching
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        Sentry.setTag(key, value)
      }
    })
  } catch (error) {
    // do nothing
    logger('error setting sentry tags', error)
  }
}

export const logInfo = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.info(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export const logWarn = (message, data) => {
  if (!configs.isSentryEnabled) return
  Sentry.logger.warn(message, augmentData(data))
  // also log to file
  logger(message, data)
}

export default {
  init,
  captureException,
  addBreadcrumb,
  startSessionReplay,
  stopSessionReplay,
  info: logInfo,
  warn: logWarn,
  setTags,
}
